import { CurrencySymbol } from '../types'

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: CurrencySymbol.USD,
  AUD: CurrencySymbol.AUD,
  EUR: CurrencySymbol.EUR,
  GBP: CurrencySymbol.GBP,
  INR: CurrencySymbol.INR
}

// REL-878: locales where the currency symbol is placed AFTER the amount (e.g. "100,00 €").
// Everything else (uk, us, au, en, ...) places it BEFORE the amount (e.g. "€100.00").
const CURRENCY_AFTER_LOCALES = new Set(['de', 'es', 'fr', 'it', 'nl'])

// Intl.NumberFormat locale to use for digit grouping / decimal separator, per app locale.
const NUMBER_FORMAT_LOCALES: Record<string, string> = {
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL',
  uk: 'en-GB',
  us: 'en-US',
  au: 'en-AU',
  en: 'en-US'
}

// The position and number formatting are derived from `locale` (the selected language),
// so callers no longer need to branch on locale themselves.
const currency = (
  value: number,
  currencyCode = process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || 'USD',
  locale?: string
): string => {
  const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode

  const numberLocale = (locale && NUMBER_FORMAT_LOCALES[locale]) || (currencyCode === 'EUR' ? 'de-DE' : 'en-US')

  // Format number only (no symbol)
  const formattedNumber = new Intl.NumberFormat(numberLocale, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)

  return locale && CURRENCY_AFTER_LOCALES.has(locale) ? `${formattedNumber} ${symbol}` : `${symbol}${formattedNumber}`
}

// WordPress content can embed a price inside rich text using a literal
// "{{CURRENCY}}" placeholder, e.g. "5 {{CURRENCY}} discount". Matches a leading number (dot or comma
// decimal) followed by that placeholder, so the number can be pulled out and
// run through the same currency formatter as `currency` above.
const CURRENCY_PLACEHOLDER_REGEX = /(\d+(?:[.,]\d+)?)\s*\{\{CURRENCY\}\}/g

// Replaces every "<number> {{CURRENCY}}" occurrence in `html` with a properly
// formatted currency value. A match whose number can't be safely cast is left
// untouched rather than passed to `currency`, to avoid runtime exceptions.
const currencyPlaceholders = (html: string, currencyCode: string, locale?: string): string =>
  html.replace(CURRENCY_PLACEHOLDER_REGEX, (match, rawNumber) => {
    const numericValue = Number(rawNumber.replace(',', '.'))
    return Number.isNaN(numericValue) ? match : currency(numericValue, currencyCode, locale)
  })

export const percentage = (value: string | number, digits = 0) => {
  const num = Number(value)
  if (Number.isNaN(num)) {
    return 'NaN'
  }

  return num.toLocaleString('en-US', { style: 'percent', minimumFractionDigits: digits })
}

const date = (rawDate, options = {}) => {
  if (!rawDate || typeof rawDate !== 'string') {
    return ''
  }

  let newDate = rawDate
  if (typeof rawDate === 'string') {
    newDate = rawDate.replace(/-/g, '/')
  }
  const standardDate = new Date(newDate)
  return new Intl.DateTimeFormat('pt-BR', options).format(new Date(standardDate.getTime()))
}

const email = (name, value) => {
  let newValue = value

  if (typeof name !== 'string' || typeof value !== 'string') {
    return ''
  }

  if (!name || !value) {
    return ''
  }

  if (name === 'email' || name === 'email_confirmation') {
    newValue = newValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    newValue = newValue.replace(/[,():<>]/g, '')
  }

  return newValue
}

const phone = (value) => {
  if (typeof value !== 'string') {
    return ''
  }

  const newValue = value.replace(/\D/g, '').substring(0, 11)
  let formattedValue

  const size = newValue.length
  if (size === 0) {
    formattedValue = newValue
  }

  if (size > 0 && size < 3) {
    formattedValue = `(${newValue}`
  }

  if (size >= 3 && size < 7) {
    formattedValue = `(${newValue.substring(0, 2)}) ${newValue.substring(2, 6)}`
  }

  if (size >= 7 && size < 11) {
    formattedValue = `(${newValue.substring(0, 2)}) ${newValue.substring(2, 6)}-${newValue.substring(6, 10)}`
  }

  if (size === 11) {
    formattedValue = `(${newValue.substring(0, 2)}) ${newValue.substring(2, 7)}-${newValue.substring(7, 11)}`
  }

  return formattedValue
}

// Mixed digit/letter postcodes must contain a space, otherwise SAP and other external systems
// reject them, e.g. NL "5301HM" -> "5301 HM" and UK "SW1A1AA" -> "SW1A 1AA".
// Values that match none of the known patterns are returned untouched, so it is safe to run while typing.
const NL_POSTCODE_REGEX = /^\s*(\d{4})\s*([a-z]{2})\s*$/i
const UK_POSTCODE_REGEX = /^\s*([a-z]{1,2}\d[a-z\d]?)\s*(\d[a-z]{2})\s*$/i

const postcode = (value) => {
  if (typeof value !== 'string') {
    return ''
  }

  const match = value.match(NL_POSTCODE_REGEX) ?? value.match(UK_POSTCODE_REGEX)

  return match ? `${match[1]} ${match[2]}`.toUpperCase() : value
}

export const format = {
  currency,
  currencyPlaceholders,
  percentage,
  date,
  email,
  phone,
  postcode
}
